/**
 * Sprint 23.8 — `/home`, the workspace home page (Workforce Experience cluster
 * finale). The supervisor's daily briefing: "what's happening across my whole
 * business right now?"
 *
 * Sparse, curated, five-section hero (Q1 D / Q2 A): identity strip → needs-
 * attention (hides when empty) → workforce pulse → open tasks →
 * scorecard. Then a sticky tab bar (Tasks · Activity · Members · Files ·
 * Settings, URL-hash driven) and a 4-block sidebar. Lives directly on the page
 * canvas (no outer card) like the Workforce route; the sidebar is its own
 * elevated strip.
 *
 * 0 agents → redirect to `/workforce` (which owns the empty state, Q6). Chat
 * scope auto-pins to the workspace (Sprint 22). Per validated mockup
 * `workspace_home_mockup_v1`.
 */

import { useMemo, type CSSProperties } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { useAuth } from "../../lib/hooks/use-auth";
import { useListTree, useWorkspaceInfo } from "../../lib/hooks/use-kuvio";
import { useChatScope } from "../hooks/use-chat-scope";
import { useWorkspaceOverview } from "../hooks/use-workspace-overview";
import { useWorkspaceTasks } from "../hooks/use-workspace-tasks";
import { EmptyState, Avatar } from "../components/primitives";
import { TabBar } from "../components/shell/TabBar";
import { WorkspaceIdentityStrip } from "../components/workspace-home/WorkspaceIdentityStrip";
import { NeedsAttentionCard } from "../components/workspace-home/NeedsAttentionCard";
import { WorkforcePulseCard } from "../components/workspace-home/WorkforcePulseCard";
import { WorkspaceActiveTasks } from "../components/workspace-home/WorkspaceActiveTasks";
import { WorkspaceScorecard } from "../components/workspace-home/WorkspaceScorecard";
import { WorkspaceHomeSidebar } from "../components/workspace-home/WorkspaceHomeSidebar";
import { WorkspaceSettingsPreview } from "../components/workspace-home/WorkspaceSettingsPreview";
import { getAgentPersona } from "../components/cockpit/CockpitHeader";
import { ActivityPanel } from "../components/cockpit/ActivityPanel";
import { FilesPanel } from "../components/cockpit/FilesPanel";
import { MembersPage } from "./MembersPage";

const TABS = ["tasks", "activity", "members", "files", "settings"] as const;
type HomeTab = (typeof TABS)[number];

function readTab(hash: string): HomeTab {
  const raw = (hash.startsWith("#") ? hash.slice(1) : hash).split(/[?&]/)[0] ?? "";
  return (TABS as readonly string[]).includes(raw) ? (raw as HomeTab) : "tasks";
}

export function WorkspaceHomePage() {
  const { currentWorkspaceId, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const tree = useListTree(currentWorkspaceId ?? undefined);
  const workspaceInfo = useWorkspaceInfo();
  const overview = useWorkspaceOverview();
  const tasksQ = useWorkspaceTasks({ limit: 5 });

  const isOwner =
    user?.workspaces.find((w) => w.id === currentWorkspaceId)?.role === "owner";
  const canEdit = !!isOwner;

  // Sprint 22 — chat scope auto-pins to the workspace ("what's happening across
  // my business?").
  useChatScope({ kind: "workspace", label: "This workspace" });

  const agents = useMemo(
    () => (tree.data ?? []).filter((n) => n.type === "agent" && !n.archived_at),
    [tree.data],
  );

  const activeTab = readTab(location.hash);
  const rootNodeId = workspaceInfo.data?.root_node_id ?? null;
  const taskCount = tasksQ.data?.active_count ?? 0;
  const memberCount = overview.data?.members_count ?? 0;

  // Q6 — 0 agents redirects to the Workforce empty state (don't design a
  // separate workspace-home-empty). Wait for the roster to load first.
  if (!tree.isLoading && !tree.error && agents.length === 0) {
    return <Navigate to="/workforce" replace />;
  }

  if (tree.isLoading || overview.isLoading) {
    return <HomeSkeleton />;
  }

  return (
    <div style={grid}>
      <main style={mainCol}>
        <WorkspaceIdentityStrip canEdit={canEdit} />
        <NeedsAttentionCard />
        <WorkforcePulseCard />
        <WorkspaceActiveTasks />
        <WorkspaceScorecard />

        <TabBar
          tabs={[
            { key: "tasks", label: "Tasks", count: taskCount },
            { key: "activity", label: "Activity" },
            { key: "members", label: "Members", count: memberCount },
            { key: "files", label: "Files" },
            { key: "settings", label: "Settings" },
          ]}
          active={activeTab}
          onSelect={(key) => navigate({ hash: `#${key}` }, { replace: false })}
          barStyle={tabBar}
          ariaLabel="Workspace home sections"
        />

        <div style={{ minWidth: 0 }}>
          {activeTab === "tasks" && <WorkspaceTasksTab />}
          {activeTab === "activity" &&
            (rootNodeId ? <ActivityPanel nodeId={rootNodeId} /> : null)}
          {activeTab === "members" && <MembersPage />}
          {activeTab === "files" &&
            (rootNodeId ? (
              <FilesPanel nodeId={rootNodeId} nodeName={overview.data?.workspace?.name} />
            ) : null)}
          {activeTab === "settings" && <WorkspaceSettingsPreview />}
        </div>
      </main>

      <aside style={sidebar}>
        <WorkspaceHomeSidebar />
      </aside>
    </div>
  );
}

/** Tasks tab — the full cross-agent open-task list (the hero shows the top 5). */
function WorkspaceTasksTab() {
  const navigate = useNavigate();
  const query = useWorkspaceTasks();
  const tasks = query.data?.tasks ?? [];

  if (tasks.length === 0) {
    return <EmptyState>No open tasks across the workforce yet.</EmptyState>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "12px 0" }}>
      {tasks.map((t) => (
        <div
          key={t.id}
          style={taskRow}
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/tasks/${t.id}`)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate(`/tasks/${t.id}`);
            }
          }}
        >
          <Avatar persona={getAgentPersona({ id: t.agent_id, path: t.agent_path })} name={t.agent_name} size="sm" />
          <span style={taskRowTitle}>{t.title}</span>
          <span style={taskRowMeta}>{t.agent_name}</span>
          <i className="ti ti-chevron-right" style={{ fontSize: 14, color: "var(--color-text-tertiary)", flexShrink: 0 }} aria-hidden="true" />
        </div>
      ))}
    </div>
  );
}

const taskRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "8px 10px",
  borderRadius: "var(--border-radius-md)",
  border: "0.5px solid var(--color-border-tertiary)",
  background: "var(--color-background-primary)",
  cursor: "pointer",
  minWidth: 0,
};

const taskRowTitle: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  flex: 1,
  minWidth: 0,
};

const taskRowMeta: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  whiteSpace: "nowrap",
  flexShrink: 0,
};

function HomeSkeleton() {
  return (
    <div style={grid}>
      <main style={mainCol}>
        <div style={{ ...skel, height: 64 }} />
        <div style={{ ...skel, height: 56 }} />
        <div style={{ ...skel, height: 160 }} />
        <div style={{ ...skel, height: 120 }} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{ ...skel, height: 52 }} />
          ))}
        </div>
      </main>
      <aside style={sidebar}>
        <div style={{ ...skel, height: 240 }} />
      </aside>
    </div>
  );
}

const grid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) 240px",
  minHeight: "100%",
  background: "var(--color-background-tertiary)",
};

const mainCol: CSSProperties = {
  padding: "18px 20px",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 14,
};

const sidebar: CSSProperties = {
  borderLeft: "0.5px solid var(--color-border-tertiary)",
  padding: "16px 14px",
  background: "var(--color-background-secondary)",
  display: "flex",
  flexDirection: "column",
  gap: 12,
  fontSize: 12,
  fontFamily: "var(--font-sans)",
};

// The sticky tab bar blends with the page canvas (tertiary) and bleeds the main
// column's 20px padding so its underline spans the full width.
const tabBar: CSSProperties = {
  background: "var(--color-background-tertiary)",
  borderBottom: "0.5px solid var(--color-border-tertiary)",
  marginLeft: -20,
  marginRight: -20,
  paddingLeft: 20,
  paddingRight: 20,
};

const skel: CSSProperties = {
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-md)",
};
